import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { 
  Building2, CheckCircle2, Phone, Mail, MapPin, ChevronRight, Award, X, ArrowRight, 
  Zap, Droplets, Thermometer, Calculator, FileText, Clipboard, ChevronDown,
  Lightbulb, Eye, FileSearch, Scale, LineChart, Users, Wrench, Settings,
  Heart, Shield, Target, Star
} from 'lucide-react';
import { useSiteData } from '../contexts/SiteDataContext';
import SEO from '../components/SEO';
import { StructuredData, generateOrganizationSchema } from '../components/StructuredData';
import { IndustryNewsSection } from '../components/IndustryNewsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== ANIMATED FADE-IN COMPONENT ====================
const FadeInOnScroll = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const getTransform = () => {
    if (isVisible) return 'translate(0, 0)';
    switch (direction) {
      case 'left': return 'translateX(-40px)';
      case 'right': return 'translateX(40px)';
      case 'down': return 'translateY(-30px)';
      default: return 'translateY(30px)';
    }
  };
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// ==================== SCROLLING MARQUEE COMPONENT ====================
const ServiceMarquee = ({ items }) => {
  // Comprehensive icon mapping matching database service names
  const icons = {
    'Mechanical Design': Thermometer,
    'Mechanical Design Services': Thermometer,
    'Electrical Design': Zap,
    'Public Health Design': Droplets,
    'Energy & Sustainability': LineChart,
    'BIM Services': FileText,
    'Project Management': Clipboard,
    'Lighting Design': Lightbulb,
    'Building Physics': Building2,
    'Interior Design': Eye,
    'Condition and Dilapidation Surveys': FileSearch,
    'Expert Witness Services': Scale,
    'Decarbonisation Analysis': LineChart,
    'Technical Reviews and Evaluation': Eye,
    'Building Services Consultancy': Settings,
    'MEP Coordination': Users
  };
  
  const displayItems = items.length > 0 ? items : [
    { title: 'Mechanical Design', id: 'mechanical' },
    { title: 'Electrical Design', id: 'electrical' },
    { title: 'Public Health Design', id: 'public-health' },
    { title: 'Lighting Design', id: 'lighting' },
    { title: 'Energy & Sustainability', id: 'energy' },
    { title: 'BIM Services', id: 'bim' }
  ];
  
  // Triple the items for smoother seamless loop
  const marqueeItems = [...displayItems, ...displayItems, ...displayItems];
  
  return (
    <div className="relative overflow-hidden py-8 bg-gradient-to-r from-slate-900 via-blue-900/95 to-slate-900">
      {/* Gradient fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none" />
      
      <div className="flex animate-marquee-smooth">
        {marqueeItems.map((item, index) => {
          const Icon = icons[item.title] || Building2;
          return (
            <Link
              key={index}
              to={`/services/${item.id || ''}`}
              className="flex items-center gap-3 px-6 py-3 mx-3 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 hover:bg-white/15 hover:border-white/25 hover:scale-105 transition-all duration-300 whitespace-nowrap group"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-blue-500/30 transition-shadow">
                <Icon className="w-4 h-4 text-white" />
              </div>
              <span className="text-white/90 font-medium group-hover:text-white transition-colors">{item.title}</span>
              <ChevronRight className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </Link>
          );
        })}
      </div>
      
      <style>{`
        @keyframes marquee-smooth {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .animate-marquee-smooth {
          animation: marquee-smooth 40s linear infinite;
          will-change: transform;
        }
        .animate-marquee-smooth:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

// ==================== INTERACTIVE VALUES CARD ====================
const ValueCard = ({ value, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Value-specific icons for visual differentiation
  const valueIcons = {
    'Collaboration': Users,
    'Accountability': Shield,
    'Integrity': Heart,
    'Excellence': Star
  };
  const ValueIcon = valueIcons[value.name] || CheckCircle2;
  
  // Get extended content - prefer CMS values, fallback to defaults
  const getExtendedContent = (val) => {
    if (val.meaning || val.importance || val.approach) {
      return {
        meaning: val.meaning || val.description || "This value guides our approach to every project.",
        importance: val.importance || "Essential for delivering successful building services projects.",
        approach: val.approach || "Integrated into our daily operations and decision-making processes."
      };
    }
    
    const defaults = {
      'Collaboration': {
        meaning: "True collaboration means working alongside clients, contractors, and stakeholders as equal partners throughout every project phase.",
        importance: "In Building Services Engineering, complex MEP systems require seamless coordination between architects, structural engineers, and multiple trade contractors.",
        approach: "We embed ourselves in project teams from day one, participating in BIM coordination meetings and design workshops."
      },
      'Accountability': {
        meaning: "Taking full ownership of our designs, recommendations, and project outcomes — standing behind every calculation and specification.",
        importance: "MEP systems account for 30-40% of construction costs and directly impact building safety, energy performance, and occupant comfort.",
        approach: "Every deliverable carries a named engineer's approval. We maintain detailed design records and conduct rigorous peer reviews."
      },
      'Integrity': {
        meaning: "Providing honest, technically sound advice even when it's not what clients want to hear. Prioritising long-term performance.",
        importance: "The construction industry faces pressure to cut corners. Buildings last 50+ years — poor decisions today become expensive problems.",
        approach: "We recommend the right solution, not the cheapest. We flag concerns early and never compromise on safety or compliance."
      },
      'Excellence': {
        meaning: "Pursuing the highest standards in technical design, documentation, and project delivery. Continuous improvement in every aspect.",
        importance: "Building services are increasingly sophisticated — heat pumps, smart controls, renewable integration require constant learning.",
        approach: "We invest in ongoing CPD, maintain CIBSE memberships, and embrace emerging technologies like digital twins."
      }
    };
    
    return defaults[val.name] || {
      meaning: val.description || "This value guides our approach to every project.",
      importance: "Essential for delivering successful building services projects.",
      approach: "Integrated into our daily operations and decision-making processes."
    };
  };
  
  const extendedContent = getExtendedContent(value);
  const learnMoreText = value.learnMoreText || "Learn more";
  const learnMoreUrl = value.learnMoreUrl;

  // Card content - same for both link and modal versions
  const CardInner = ({ isLink = false }) => (
    <div className="h-full flex flex-col">
      {/* Icon area - fixed height with gradient ring */}
      <div className="flex-shrink-0 mb-5">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
          <div className="relative w-14 h-14 bg-gradient-to-br from-blue-600 via-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
            <ValueIcon className="w-7 h-7 text-white" />
          </div>
        </div>
      </div>
      
      {/* Title - fixed height */}
      <h3 className="flex-shrink-0 text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
        {value.name}
      </h3>
      
      {/* Description - clamped to 3 lines for consistency */}
      <p className="flex-grow text-gray-500 text-sm leading-relaxed line-clamp-3 mb-5">
        {value.description}
      </p>
      
      {/* CTA - fixed at bottom with animated underline */}
      <div className="flex-shrink-0 mt-auto pt-4 border-t border-gray-100/80">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 group-hover:gap-3 transition-all relative">
          {learnMoreText}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300" />
        </span>
      </div>
    </div>
  );
  
  // If there's an external URL, render as link
  if (learnMoreUrl) {
    return (
      <FadeInOnScroll delay={index * 100}>
        <a 
          href={learnMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group block h-full min-h-[280px] relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100/80 hover:border-blue-200/80 transform hover:-translate-y-2"
        >
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative h-full">
            <CardInner isLink />
          </div>
        </a>
      </FadeInOnScroll>
    );
  }
  
  return (
    <>
      <FadeInOnScroll delay={index * 100}>
        <div 
          onClick={() => setIsExpanded(true)}
          className="group h-full min-h-[280px] relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer border border-gray-100/80 hover:border-blue-200/80 transform hover:-translate-y-2"
        >
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative h-full">
            <CardInner />
          </div>
        </div>
      </FadeInOnScroll>
      
      {/* Enhanced Modal with smooth animations */}
      {isExpanded && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setIsExpanded(false)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-blue-600 to-blue-700 p-8 text-white rounded-t-3xl">
              <button 
                onClick={() => setIsExpanded(false)}
                className="absolute top-6 right-6 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                <ValueIcon className="w-7 h-7" />
              </div>
              <h3 className="text-3xl font-bold">{value.name}</h3>
              <p className="text-blue-100 mt-2 text-lg">{value.description}</p>
            </div>
            
            {/* Content */}
            <div className="p-8 space-y-8">
              <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
                <h4 className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">
                  {value.meaningLabel || "What This Means"}
                </h4>
                <p className="text-gray-700 leading-relaxed">{extendedContent.meaning}</p>
              </div>
              
              <div className="border-l-4 border-blue-600 pl-6 animate-slide-up" style={{ animationDelay: '200ms' }}>
                <h4 className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">
                  {value.importanceLabel || "Why It Matters in BSE"}
                </h4>
                <p className="text-gray-700 leading-relaxed">{extendedContent.importance}</p>
              </div>
              
              <div className="animate-slide-up" style={{ animationDelay: '300ms' }}>
                <h4 className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">
                  {value.approachLabel || "Our Approach"}
                </h4>
                <p className="text-gray-700 leading-relaxed">{extendedContent.approach}</p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-8 pb-8">
              <Button onClick={() => setIsExpanded(false)} className="w-full bg-gray-900 hover:bg-gray-800 py-6 text-lg rounded-xl">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out forwards;
        }
        .animate-slide-up {
          animation: slideUp 0.4s ease-out forwards;
          opacity: 0;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

// ==================== FEATURED HIGHLIGHT BLOCK ====================
const FeaturedHighlight = ({ settings }) => {
  const fh = settings?.featuredHighlight || {};
  const title = fh.title || "Engineering solutions that work harder for your building";
  const subtitle = fh.subtitle || "Why Choose FURD";
  const description = fh.description || "We combine technical excellence with practical buildability, delivering MEP designs that perform efficiently, install cost-effectively, and stand the test of time.";
  const primaryButtonText = fh.primaryButtonText || "Explore Our Services";
  const primaryButtonLink = fh.primaryButtonLink || "/services";
  const secondaryButtonText = fh.secondaryButtonText || "Get in Touch";
  const secondaryButtonLink = fh.secondaryButtonLink || "/contact";
  
  return (
    <FadeInOnScroll>
      <div className="relative overflow-hidden rounded-3xl group">
        {/* Premium gradient background with animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-transparent to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
        
        {/* Animated decorative elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-blue-500/25 to-transparent rounded-full blur-3xl group-hover:scale-125 transition-transform duration-1000" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-blue-400/15 to-transparent rounded-full blur-3xl group-hover:scale-125 transition-transform duration-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-gradient-radial from-blue-600/5 to-transparent rounded-full" />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        
        <div className="relative z-10 p-10 md:p-14 lg:p-20">
          <div className="max-w-2xl">
            <FadeInOnScroll delay={100}>
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500/20 backdrop-blur-sm rounded-full text-blue-300 text-sm font-semibold mb-8 border border-blue-400/20">
                <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                {subtitle}
              </span>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-8 leading-tight tracking-tight">
                {title}
              </h2>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={300}>
              <p className="text-lg md:text-xl text-blue-100/70 mb-10 leading-relaxed max-w-xl">
                {description}
              </p>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={400}>
              <div className="flex flex-wrap gap-4">
                <Link to={primaryButtonLink}>
                  <Button size="lg" className="bg-white text-slate-900 hover:bg-blue-50 hover:scale-105 px-8 py-6 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-white/20 group/btn">
                    {primaryButtonText}
                    <ChevronRight className="ml-2 w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to={secondaryButtonLink}>
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-6 text-lg transition-all duration-300 backdrop-blur-sm">
                    {secondaryButtonText}
                  </Button>
                </Link>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </div>
    </FadeInOnScroll>
  );
};

// ==================== SERVICE ACCORDION ====================
const ServiceAccordion = ({ services, title, subtitle }) => {
  const [openIndex, setOpenIndex] = useState(null);
  
  if (!services || services.length === 0) return null;
  
  return (
    <FadeInOnScroll>
      <div>
        <div className="mb-8">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">{subtitle}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">{title}</h2>
        </div>
        
        <div className="space-y-3">
          {services.map((service, index) => (
            <div 
              key={service.id || index}
              className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:border-blue-200 transition-colors"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                    <Building2 className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-semibold text-gray-900 text-lg group-hover:text-blue-600 transition-colors">
                    {service.title}
                  </span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-48' : 'max-h-0'}`}>
                <div className="px-6 pb-6 pt-0">
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link 
                    to={`/services/${service.id}`}
                    className="inline-flex items-center gap-2 text-blue-600 font-medium hover:gap-3 transition-all"
                  >
                    Learn more <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 text-center">
          <Link to="/services" className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all">
            View All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </FadeInOnScroll>
  );
};

// ==================== MAIN HOME COMPONENT ====================
const Home = () => {
  const { toast } = useToast();
  const { 
    phone, email, address, 
    services,
    siteSettings,
    companyInfo,
    servicesOverviewSettings,
    contactSettings,
  } = useSiteData();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get data from CMS
  const designServices = services.filter(s => s.category === 'design');
  
  // Services Overview settings from CMS
  const svcOverview = servicesOverviewSettings || {};
  const showServicesSection = svcOverview.showSection !== false;
  const servicesSectionTitle = svcOverview.sectionTitle || "Our Services";
  const servicesSectionIntro = svcOverview.sectionIntro || "Comprehensive MEP engineering and design consultancy services.";
  const designServicesTitle = svcOverview.designServicesTitle || "Design Services";
  const designServicesSubtitle = svcOverview.designServicesSubtitle || "Engineering Excellence";
  const showDesignServices = svcOverview.showDesignServices !== false;
  const consultancyServicesTitle = svcOverview.consultancyServicesTitle || "Consultancy Services";
  const consultancyServicesSubtitle = svcOverview.consultancyServicesSubtitle || "Expert Guidance";
  const showConsultancyServices = svcOverview.showConsultancyServices !== false;
  const viewAllText = svcOverview.viewAllText || "View All Services";
  const viewAllLink = svcOverview.viewAllLink || "/services";
  const showViewAllLink = svcOverview.showViewAllLink !== false;
  const consultancyServices = services.filter(s => s.category === 'consultancy');
  const allServices = [...designServices, ...consultancyServices];

  // Default values section
  const defaultValues = [
    { name: 'Collaboration', description: 'Collaboration is the backbone of our projects' },
    { name: 'Accountability', description: 'We hold ourselves to the highest standards' },
    { name: 'Integrity', description: 'We are committed to ethical practices' },
    { name: 'Excellence', description: 'We strive for excellence at every step' }
  ];
  const displayValues = siteSettings?.values?.length > 0 ? siteSettings.values : defaultValues;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Submit to Formspree
      const response = await fetch('https://formspree.io/f/xnjbqppr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          _subject: 'FURD Contact Form: Homepage Enquiry',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          enquiryType: 'general',
          message: formData.message,
          submittedAt: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        // Also save to database for records
        try {
          await axios.post(`${API}/contact`, {
            ...formData,
            submittedAt: new Date().toISOString()
          });
        } catch (dbError) {
          console.log('Database save optional:', dbError);
        }
        
        toast({
          title: "Message Sent!",
          description: contactSettings?.autoReplyMessage || "Thank you for contacting us. We'll get back to you shortly.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Hero configuration - restore to original working state
  const heroVideoUrl = siteSettings?.heroVideoUrl || "https://customer-assets.emergentagent.com/job_furd-mep/artifacts/furd_hero.mp4";
  const heroPosterUrl = siteSettings?.heroVideoPoster || "https://customer-assets.emergentagent.com/job_furd-mep/artifacts/furd_hero_poster.jpg";
  const heroTitle = siteSettings?.heroTitle || "Engineering\nExcellence";
  const heroSubtitle = siteSettings?.heroSubtitle || "Professional MEP Engineering & Design Consultancy delivering innovative solutions for commercial, residential, and industrial projects.";
  const heroTitleAlignment = siteSettings?.heroTitleAlignment || 'center';
  const heroSubtitleColor = siteSettings?.heroSubtitleColor || '#E5E7EB';
  
  // Hero Title styling from CMS
  const heroTitleColor = siteSettings?.heroTitleColor || '#FFFFFF';
  const heroTitleFontSizeDesktop = siteSettings?.heroTitleFontSizeDesktop || 72;
  const heroTitleFontSizeMobile = siteSettings?.heroTitleFontSizeMobile || 48;
  const heroTitleColorSpans = siteSettings?.heroTitleColorSpans || [];

  // Function to render hero title with color spans from CMS
  const renderHeroTitleWithColors = () => {
    const fullText = heroTitle;
    const chars = fullText.split('');
    
    // If no color spans defined, render with default color
    if (heroTitleColorSpans.length === 0) {
      return heroTitle.split('\n').map((line, i) => (
        <span key={i} className={i > 0 ? 'block' : ''} style={{ color: heroTitleColor }}>
          {line}
        </span>
      ));
    }
    
    // Render with color spans
    let charIndex = 0;
    return heroTitle.split('\n').map((line, lineIdx) => {
      const lineChars = line.split('').map((char, idx) => {
        const globalIdx = charIndex++;
        const span = heroTitleColorSpans.find(
          s => globalIdx >= s.startIndex && globalIdx <= s.endIndex
        );
        const charColor = span ? span.color : heroTitleColor;
        return (
          <span key={`${lineIdx}-${idx}`} style={{ color: charColor }}>
            {char}
          </span>
        );
      });
      // Account for newline character in global index
      if (lineIdx < heroTitle.split('\n').length - 1) charIndex++;
      return (
        <span key={lineIdx} className={lineIdx > 0 ? 'block' : ''}>
          {lineChars}
        </span>
      );
    });
  };

  // Homepage component - no scroll issues (verified: extension was causing false positives)

  return (
    <div className="bg-white">
      <SEO 
        title="FURD Ltd - MEP Engineering & Design Consultancy"
        description="Professional MEP consultancy services in Building Services Engineering. Mechanical, Electrical & Public Health design solutions."
      />
      <StructuredData data={generateOrganizationSchema({ phone, email, address, name: companyInfo?.name || 'FURD Ltd' })} />

      {/* Hero Section with Video Background - Premium dynamic styling */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background with subtle zoom animation */}
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={heroPosterUrl}
          className="absolute inset-0 w-full h-full object-cover scale-105 animate-hero-zoom"
          onError={(e) => { e.target.style.display = 'none'; }}
        >
          <source src={heroVideoUrl} type="video/mp4" />
        </video>
        
        {/* Enhanced Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-transparent" />
        
        {/* Hero Content with staggered animations */}
        <div 
          className="relative z-10 text-white px-4 max-w-5xl mx-auto w-full"
          style={{ textAlign: heroTitleAlignment }}
        >
          {/* Hero Logo */}
          {siteSettings?.heroLogoEnabled && siteSettings?.heroLogoUrl && (
            <FadeInOnScroll delay={100}>
              <div 
                className="mb-8"
                style={{ textAlign: siteSettings?.heroLogoAlignment || 'center' }}
              >
                <img 
                  src={siteSettings.heroLogoUrl}
                  alt="Logo"
                  className="inline-block object-contain"
                  style={{ height: `${siteSettings?.heroLogoSize || 120}px` }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </FadeInOnScroll>
          )}
          
          {/* Hero Title - Styled from CMS with animation */}
          <FadeInOnScroll delay={200}>
            <h1 
              className="font-bold mb-6 leading-tight"
              style={{
                fontSize: `${heroTitleFontSizeMobile}px`,
                color: heroTitleColor
              }}
            >
              <style>{`
                @media (min-width: 768px) {
                  .hero-title-responsive { font-size: ${heroTitleFontSizeDesktop}px !important; }
                }
              `}</style>
              <span className="hero-title-responsive" style={{ fontSize: 'inherit' }}>
                {renderHeroTitleWithColors()}
              </span>
            </h1>
          </FadeInOnScroll>
          
          {/* Hero Subtitle */}
          <FadeInOnScroll delay={400}>
            <p 
              className="text-xl md:text-2xl mb-10 max-w-3xl leading-relaxed"
              style={{ 
                margin: heroTitleAlignment === 'center' ? '0 auto 40px auto' : '0 0 40px 0',
                color: heroSubtitleColor
              }}
            >
              {heroSubtitle}
            </p>
          </FadeInOnScroll>
          
          {/* CTA Buttons with hover effects */}
          <FadeInOnScroll delay={600}>
            <div 
              className="flex gap-4 flex-wrap"
              style={{ justifyContent: heroTitleAlignment }}
            >
              <Link to="/services">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg shadow-xl hover:shadow-2xl hover:shadow-blue-600/30 transition-all duration-300 hover:scale-105 group">
                  Our Services
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white/50 text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                  Contact Us
                </Button>
              </Link>
            </div>
          </FadeInOnScroll>
        </div>
        
        {/* Premium scroll indicator with glow */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="relative">
            <div className="absolute inset-0 bg-white/20 rounded-full blur-xl animate-pulse" />
            <div className="relative animate-bounce p-3 rounded-full border border-white/30 backdrop-blur-sm">
              <ChevronDown className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Marquee Strip */}
      <ServiceMarquee items={allServices} />

      {/* Values Section - Premium Design */}
      <section className="py-28 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden" style={{ contain: 'paint' }}>
        {/* Animated decorative background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[300px] -right-[300px] w-[600px] h-[600px] bg-gradient-to-br from-blue-100/60 to-blue-50/40 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute -bottom-[250px] -left-[250px] w-[500px] h-[500px] bg-gradient-to-tr from-slate-100/60 to-blue-50/30 rounded-full blur-3xl" />
          {/* Subtle grid pattern overlay */}
          <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <FadeInOnScroll>
            <div className="text-center mb-20">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600/10 to-blue-500/10 rounded-full text-blue-600 font-semibold text-sm uppercase tracking-widest mb-6 border border-blue-200/50 backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                What Drives Us
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">
                Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">Values</span>
              </h2>
              <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
                Click any value to discover how it shapes our approach to Building Services Engineering.
              </p>
            </div>
          </FadeInOnScroll>
          
          {/* Grid with equal height cards using CSS Grid auto-rows */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 auto-rows-fr">
            {displayValues.map((value, index) => (
              <ValueCard key={index} value={value} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Highlight Block - CMS Controlled */}
      {siteSettings?.featuredHighlight?.showSection !== false && (
        <section className="py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <FeaturedHighlight settings={siteSettings} />
        </section>
      )}

      {/* Services Accordions - CMS Controlled */}
      {showServicesSection && (
        <section className="py-28 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
          {/* Subtle pattern */}
          <div className="absolute inset-0 opacity-[0.015]" style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Section Header from CMS */}
            <FadeInOnScroll>
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 tracking-tight">{servicesSectionTitle}</h2>
                <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">{servicesSectionIntro}</p>
              </div>
            </FadeInOnScroll>
            
            <div className="grid lg:grid-cols-2 gap-16">
              {showDesignServices && (
                <ServiceAccordion 
                  services={designServices} 
                  title={designServicesTitle}
                  subtitle={designServicesSubtitle}
                />
              )}
              {showConsultancyServices && (
                <ServiceAccordion 
                  services={consultancyServices} 
                  title={consultancyServicesTitle}
                  subtitle={consultancyServicesSubtitle}
                />
              )}
            </div>
            
            {/* View All Link from CMS */}
            {showViewAllLink && (
              <div className="text-center mt-12">
                <Link 
                  to={viewAllLink} 
                  className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors"
                >
                  {viewAllText}
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Stay Informed - Industry News Section */}
      <IndustryNewsSection maxItems={3} showViewAll={true} />
    </div>
  );
};

export default Home;
